import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import { matchesSidebarSearch } from "@verbleif/lib/src/components/Sidebar/sidebarSearch";

/** A member of a department, as the tree lists it under that department. */
export interface TaskListScopeMember {
  id: number
  name: string
}

/** A department (task list) with the members the reader may see in it. */
export interface TaskListScopeDepartment {
  id: number
  name: string
  /** Icon of the department; the department icon of Tasks when absent. */
  icon?: IconName | null
  members: TaskListScopeMember[]
}

/**
 * The choice of the tree, the scope of the `task_list` view: a department (`taskList`), a user
 * inside that department (both), or a user without a department (`user` only).
 */
export interface TaskListScopeSelection {
  taskList: number | null
  user: number | null
}

export const TASK_LIST_SCOPE_DEPARTMENT_ICON: IconName = "presentation";
export const TASK_LIST_SCOPE_USER_ICON: IconName = "user";

export type TaskListScopeRow
  = | {
    kind: "department"
    key: string
    level: 1
    department: TaskListScopeDepartment
    /** Members shown under the department, after the search. */
    members: TaskListScopeMember[]
    expanded: boolean
    selection: TaskListScopeSelection
  }
  | {
    kind: "member"
    key: string
    level: 2
    department: TaskListScopeDepartment
    member: TaskListScopeMember
    selection: TaskListScopeSelection
  }
  | {
    kind: "groupless"
    key: string
    level: 1
    member: TaskListScopeMember
    selection: TaskListScopeSelection
  };

export function departmentRowKey(departmentId: number): string {
  return `department-${departmentId}`;
}

export function memberRowKey(departmentId: number, userId: number): string {
  return `department-${departmentId}-user-${userId}`;
}

export function grouplessRowKey(userId: number): string {
  return `user-${userId}`;
}

/** Only the chosen pair matches: a user in two departments is only chosen in the one it was picked in. */
export function isSameTaskListScope(a: TaskListScopeSelection | null, b: TaskListScopeSelection | null): boolean {
  if (a === null || b === null) {
    return false;
  }
  return a.taskList === b.taskList && a.user === b.user;
}

function byName<T extends { name: string }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The visible rows of the tree: departments by name, the members of an expanded department under
 * it, and the users without a department at the bottom. A search keeps the departments and users
 * whose name matches; a department with a matching member shows open, and when only a member
 * matches it shows just the matching members, so the found member is visible. `expanded` holds what
 * the reader opened or closed by hand, which wins over that.
 */
export function buildTaskListScopeRows(
  departments: readonly TaskListScopeDepartment[],
  groupless: readonly TaskListScopeMember[],
  expanded: ReadonlyMap<number, boolean>,
  search = "",
): TaskListScopeRow[] {
  const searching = search.trim() !== "";
  const rows: TaskListScopeRow[] = [];

  for (const department of byName(departments)) {
    const sortedMembers = byName(department.members);
    let members = sortedMembers;
    let autoOpen = false;
    if (searching) {
      const departmentMatches = matchesSidebarSearch(department.name, search);
      const matchingMembers = sortedMembers.filter(member => matchesSidebarSearch(member.name, search));
      if (!departmentMatches && matchingMembers.length === 0) {
        continue;
      }
      if (!departmentMatches) {
        members = matchingMembers;
      }
      autoOpen = matchingMembers.length > 0;
    }
    const open = expanded.get(department.id) ?? autoOpen;
    const hasMembers = members.length > 0;
    rows.push({
      kind: "department",
      key: departmentRowKey(department.id),
      level: 1,
      department,
      members,
      expanded: hasMembers && open,
      selection: { taskList: department.id, user: null },
    });
    if (!hasMembers || !open) {
      continue;
    }
    for (const member of members) {
      rows.push({
        kind: "member",
        key: memberRowKey(department.id, member.id),
        level: 2,
        department,
        member,
        selection: { taskList: department.id, user: member.id },
      });
    }
  }

  for (const member of byName(groupless)) {
    if (searching && !matchesSidebarSearch(member.name, search)) {
      continue;
    }
    rows.push({
      kind: "groupless",
      key: grouplessRowKey(member.id),
      level: 1,
      member,
      selection: { taskList: null, user: member.id },
    });
  }

  return rows;
}

/** The department to open so the chosen member is visible when the tree appears. */
export function initiallyExpandedDepartments(selected: TaskListScopeSelection | null): Map<number, boolean> {
  if (selected?.taskList == null || selected.user == null) {
    return new Map();
  }
  return new Map([[selected.taskList, true]]);
}
